import { FC } from 'react';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, List, ListItem, Stack } from '@mui/material';
import {
  COLOR_BY_STATUS,
  claimMismatches,
  claimStatuses,
  claimsFiltersSchema,
  useClaims,
  useExportToXls,
  useUpdateClaim,
  useUpdateClaims,
} from 'store/claims';
import { APP, ROUTES } from 'config';
import { DownloadXlsAction } from 'components/actions';
import { useDefaultCurrency } from 'state/Administrators';
import moment from 'moment';
import { formatCurrency } from 'utils/format';
import { useModalContext } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import {
  BenefitsFilter,
  CompaniesFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { useSearchParams } from 'utils/navigation';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import { ClaimRejectModal } from './atoms';

export const ClaimsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();
  const { currency } = useDefaultCurrency();

  const { searchParams: filters, navigate } =
    useSearchParams(claimsFiltersSchema);

  const { data: claims, isLoading } = useClaims(filters);

  const { mutate: updateClaim, isLoading: isUpdatingClaim } = useUpdateClaim();
  const { mutate: updateClaims, isLoading: isUpdatingClaims } =
    useUpdateClaims();
  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={claims?.total} />

          <Stack direction="row" alignItems="center" gap={1}>
            <Filters
              value={filters}
              initialValues={claimsFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: v })}
              onChangeCallback={() => setSelectedRowIds({})}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('claims.filters.mismatch.label')}
                    value={value.mismatch}
                    initialValue={initialValues.mismatch}
                    onChange={(v) => setFilter('mismatch', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={claimMismatches.map((mismatch) => ({
                          key: mismatch,
                          label: t(`claims.mismatch.${mismatch}`),
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('employees.company')}
                    value={value.company}
                    initialValue={initialValues.company}
                    onChange={(v) => setFilter('company', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <CompaniesFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('claims.filters.claim_date.label')}
                    value={value.claimDate}
                    initialValue={initialValues.claimDate}
                    onChange={(v) => setFilter('claimDate', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('claims.table.purchase_date.title')}
                    value={value.purchaseDate}
                    initialValue={initialValues.purchaseDate}
                    onChange={(v) => setFilter('purchaseDate', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('global.common.benefit_name')}
                    value={value.benefit}
                    initialValue={initialValues.benefit}
                    onChange={(v) => setFilter('benefit', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <BenefitsFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('common.status')}
                    value={value.status}
                    initialValue={initialValues.status}
                    onChange={(v) => setFilter('status', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={claimStatuses.map((status) => ({
                          key: status,
                          label: t(`claims.status.${status}`),
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('claims.filters.search.placeholder')}
                  />
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <DownloadXlsAction
              listLength={claims?.total ?? 0}
              exportToExcel={() => exportToXls({ filters })}
              loading={isExportingToXls}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={claims?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'employee',
            header: t('entity_type.employee'),
            cell: ({ row: { original: claim } }) =>
              claim.employee ? (
                <Link
                  label={claim.employee.fullName}
                  to={ROUTES.employees.details.replace(
                    ':id',
                    claim.employee.id,
                  )}
                />
              ) : (
                <Typography variant="p1" color="textSecondary">
                  {t('marketplace.employeeDeleted')}
                </Typography>
              ),
          },
          {
            id: 'claimedAt',
            header: t('claims.table.claim_date.title'),
            cell: ({ row: { original: claim } }) => (
              <Link
                label={moment(claim.claimedAt).format('l LT')}
                to={ROUTES.claims.details.replace(':id', claim.id)}
              />
            ),
          },
          {
            id: 'purchasedAt',
            header: t('claims.table.purchase_date.title'),
            cell: ({ row: { original: claim } }) =>
              moment(claim.purchasedAt).format('l'),
          },
          {
            id: 'source',
            header: t('wallet.employeeWalletFundsSource'),
            cell: ({ row: { original: claim } }) => (
              <List sx={{ py: 0 }}>
                {claim.sources.map((s) => (
                  <ListItem sx={{ p: 0, mb: 1 }} key={s.id}>
                    <Link
                      label={s.name}
                      to={
                        s.id === RECOGNITION_SHOP_ID
                          ? ROUTES.recognitions.settings
                          : ROUTES.benefits.details.replace(':id', s.id)
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ),
          },
          {
            id: 'supplier',
            header: t('marketplaceItems.supplier'),
            cell: ({ row: { original: claim } }) => {
              return (
                <div
                  style={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    maxWidth: 200,
                  }}
                >
                  {claim.supplier}
                </div>
              );
            },
          },
          {
            id: 'requestedAmount',
            header: t('claims.table.requested_amount.title'),
            meta: { align: 'right' },
            cell: ({ row: { original: claim } }) =>
              formatCurrency(i18n.language, claim.requestedAmount, currency),
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: claim } }) => {
              return (
                <Typography color={COLOR_BY_STATUS[claim.status]}>
                  {t(`claims.status.${claim.status}`)}
                </Typography>
              );
            },
          },
          {
            id: 'actions',
            cell: ({ row: { original: claim } }) => {
              if (claim.status !== 'pendingHRAction') return null;

              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t('global.actions.approve'),
                      onClick: () => {
                        updateClaim(
                          { claimId: claim.id, status: 'approved' },
                          {
                            onSuccess: () => predefinedToasts.success.updated(),
                          },
                        );
                      },
                    },
                    {
                      label: t('global.actions.reject'),
                      onClick: async () => {
                        const response = await openModal({
                          component: ClaimRejectModal,
                          props: { title: claim.supplier },
                        });

                        if (response.action === 'REJECT') {
                          updateClaim({
                            claimId: claim.id,
                            status: response.payload.status,
                            adminComment: response.payload.comment,
                          });
                        }
                      },
                      color: 'error',
                    },
                  ]}
                />
              );
            },
          },
        ]}
        pagination={
          claims && {
            count: claims.total,
            page: claims.page,
            pageSize: claims.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          isLoading: isUpdatingClaims || isUpdatingClaim,
          actions: [
            {
              label: t('claims.actions.mark_as_paid'),
              onClick: () => {
                updateClaims(
                  {
                    updates: Object.keys(selectedRowIds).map((claimId) => ({
                      claimId,
                      status: 'paid',
                    })),
                  },
                  {
                    onSuccess: () => {
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
            {
              label: t('claims.actions.mark_as_approved'),
              onClick: () => {
                updateClaims(
                  {
                    updates: Object.keys(selectedRowIds).map((claimId) => ({
                      claimId,
                      status: 'approved',
                    })),
                  },
                  {
                    onSuccess: () => {
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            },
          ],
        }}
      />
    </TableWrapper>
  );
};
